import { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '@/components/feature/AppLayout';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import UserModal from './components/UserModal';
import UserDetailDrawer from './components/UserDetailDrawer';

export interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  role: string;
  is_active: boolean;
  client_id: string | null;
  created_at: string;
  // joins
  _client_name?: string;
  _project_count?: number;
}

export interface Client {
  id: string;
  name: string;
}

export default function UsuariosPage() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [detailUser, setDetailUser] = useState<UserProfile | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);

    const [usersRes, clientsRes] = await Promise.all([
      supabase.from('profiles').select('*').order('full_name'),
      supabase.from('clients').select('id, name').order('name'),
    ]);

    const rawUsers: UserProfile[] = usersRes.data || [];
    const clientList: Client[] = clientsRes.data || [];

    const cMap = Object.fromEntries(clientList.map(c => [c.id, c.name]));

    // Contar projetos por usuário
    const { data: members } = await supabase
      .from('project_members')
      .select('user_id');

    const projCount: Record<string, number> = {};
    (members || []).forEach(m => {
      projCount[m.user_id] = (projCount[m.user_id] || 0) + 1;
    });

    const enriched = rawUsers.map(u => ({
      ...u,
      _client_name: u.client_id ? cMap[u.client_id] || '—' : '—',
      _project_count: projCount[u.id] || 0,
    }));

    setUsers(enriched);
    setClients(clientList);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || u.full_name.toLowerCase().includes(q)
      || u.email.toLowerCase().includes(q)
      || (u.phone || '').includes(q);
    const matchRole = !filterRole || u.role === filterRole;
    const matchStatus = !filterStatus
      || (filterStatus === 'active' ? u.is_active : !u.is_active);
    const matchClient = !filterClient || u.client_id === filterClient;
    return matchSearch && matchRole && matchStatus && matchClient;
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-user`;

  const handleSave = async (data: {
    full_name: string;
    email: string;
    phone: string;
    role: string;
    is_active: boolean;
    client_id: string | null;
    password?: string;
  }) => {
    setSaving(true);
    setSaveError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (editingUser) {
        // Edição via Edge Function (service_role bypassa RLS)
        const res = await fetch(EDGE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'update',
            user_id: editingUser.id,
            full_name: data.full_name,
            phone: data.phone,
            role: data.role,
            is_active: data.is_active,
            client_id: data.client_id,
          }),
        });

        let result: { error?: string; success?: boolean } = {};
        try {
          result = await res.json();
        } catch {
          setSaveError(`Erro HTTP ${res.status}: resposta inválida da função`);
          setSaving(false);
          return;
        }
        if (!res.ok || result.error) {
          setSaveError(`Erro ao salvar: ${result.error || `HTTP ${res.status}`}`);
          setSaving(false);
          return;
        }

        await logAudit('update', 'profiles', editingUser.id, { full_name: data.full_name });
      } else {
        // Criação via Edge Function (service_role cria usuário confirmado)
        const res = await fetch(EDGE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'create',
            email: data.email,
            password: data.password,
            full_name: data.full_name,
            phone: data.phone,
            role: data.role,
            is_active: data.is_active,
            client_id: data.client_id,
          }),
        });

        let result: { error?: string; success?: boolean; user_id?: string } = {};
        try {
          result = await res.json();
        } catch {
          setSaveError(`Erro HTTP ${res.status}: resposta inválida da função`);
          setSaving(false);
          return;
        }
        if (!res.ok || result.error) {
          const msg = result.error || '';
          setSaveError(
            msg.includes('already registered') || msg.includes('already been registered')
              ? 'Este e-mail já está cadastrado.'
              : `Erro ao criar usuário: ${msg || `HTTP ${res.status}`}`
          );
          setSaving(false);
          return;
        }

        await logAudit('insert', 'profiles', result.user_id, { full_name: data.full_name, email: data.email });
      }

      loadData();
      setModalOpen(false);
      setEditingUser(null);
    } catch (err) {
      setSaveError(`Erro de conexão: ${String(err)}`);
    }

    setSaving(false);
  };

  const handleToggleActive = async (user: UserProfile) => {
    const { error } = await supabase.from('profiles').update({
      is_active: !user.is_active,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);
    if (!error) {
      await logAudit('update', 'profiles', user.id, { is_active: !user.is_active });
      loadData();
    }
  };

  const openEdit = (u: UserProfile) => {
    setEditingUser(u);
    setModalOpen(true);
  };

  const openDetail = (u: UserProfile) => {
    setDetailUser(u);
  };

  const totalAdmins = users.filter(u => u.role === 'admin').length;
  const totalClients = users.filter(u => u.role === 'client').length;
  const totalActive = users.filter(u => u.is_active).length;

  const ROLE_CONFIG: Record<string, { label: string; color: string }> = {
    admin: { label: 'Admin', color: 'bg-cyan-100 text-cyan-700' },
    client: { label: 'Cliente', color: 'bg-violet-100 text-violet-700' },
    member: { label: 'Membro', color: 'bg-slate-100 text-slate-600' },
  };

  const initials = (name: string) =>
    name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-slate-800 text-xl font-bold">Usuários</h1>
          <p className="text-slate-400 text-xs mt-0.5">Gerencie perfis, papéis e vínculos com clientes</p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setModalOpen(true); setSaveError(null); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          Novo Usuário
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: users.length, icon: 'ri-team-line', color: 'bg-slate-50', iconColor: 'text-slate-500' },
          { label: 'Ativos', value: totalActive, icon: 'ri-checkbox-circle-line', color: 'bg-emerald-50', iconColor: 'text-emerald-500' },
          { label: 'Admins', value: totalAdmins, icon: 'ri-shield-user-line', color: 'bg-cyan-50', iconColor: 'text-cyan-500' },
          { label: 'Clientes', value: totalClients, icon: 'ri-building-line', color: 'bg-violet-50', iconColor: 'text-violet-500' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${card.color} mb-3`}>
              <i className={`${card.icon} ${card.iconColor} text-base`}></i>
            </div>
            <p className="text-slate-400 text-xs">{card.label}</p>
            <p className="text-slate-800 text-2xl font-bold mt-0.5">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou telefone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          />
        </div>
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os papéis</option>
          <option value="admin">Admin</option>
          <option value="client">Cliente</option>
          <option value="member">Membro</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </select>
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os clientes</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-team-line text-5xl mb-4"></i>
          <p className="text-sm font-medium text-slate-500">Nenhum usuário encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Usuário</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Papel</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Cliente vinculado</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Projetos</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Status</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Cadastro</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => {
                  const roleConf = ROLE_CONFIG[user.role] || { label: user.role, color: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr
                      key={user.id}
                      className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => openDetail(user)}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                            user.is_active ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-400'
                          }`}>
                            {initials(user.full_name || 'U')}
                          </div>
                          <div>
                            <p className="text-slate-800 font-medium">{user.full_name}</p>
                            <p className="text-slate-400 text-xs">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleConf.color}`}>
                          {roleConf.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs">
                        {user.client_id ? (
                          <span className="flex items-center gap-1">
                            <i className="ri-building-line text-slate-400"></i>
                            {user._client_name}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {(user._project_count || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            <i className="ri-folder-line"></i>
                            {user._project_count}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <i className={user.is_active ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'}></i>
                          {user.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-5 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(user)}
                            title="Editar"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleToggleActive(user)}
                            title={user.is_active ? 'Desativar' : 'Ativar'}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              user.is_active
                                ? 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
                                : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'
                            }`}
                          >
                            <i className={user.is_active ? 'ri-forbid-line text-sm' : 'ri-checkbox-circle-line text-sm'}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal criação/edição */}
      {modalOpen && (
        <UserModal
          user={editingUser}
          clients={clients}
          onClose={() => { setModalOpen(false); setEditingUser(null); setSaveError(null); }}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
      )}

      {/* Drawer detalhe */}
      {detailUser && (
        <UserDetailDrawer
          user={detailUser}
          clients={clients}
          onClose={() => setDetailUser(null)}
          onEdit={() => { openEdit(detailUser); setDetailUser(null); }}
          onToggleActive={() => { handleToggleActive(detailUser); setDetailUser(null); }}
          onRefresh={loadData}
        />
      )}
    </AppLayout>
  );
}